import type { FC } from 'react';

import clsx from 'clsx';

import useLocalised from 'customHooks/useLocalised/useLocalised';

import {
  as_a_registered_member,
  terms_and_conditions_dialogue_text_1,
  terms_and_conditions_dialogue_text_2,
  terms_and_conditions_dialogue_text_3,
  terms_and_conditions_dialogue_text_4,
} from 'data/translationKeys';

import styles from './_TermsAndConditions.module.scss';

import type { TermsAndConditionsType } from 'types/types';
import AccordionList from 'components/accordion-list';
import { BaseText } from 'components/text/BaseText';

type TermsAndConditionsProps = {
  termsAndConditions: TermsAndConditionsType[];
  className?: string;
};

const TermsAndConditions: FC<TermsAndConditionsProps> = ({
  termsAndConditions,
  className,
}) => {
  const termsAndConditionsContent = useLocalised(as_a_registered_member);
  const termsAndConditionsDialogueText1 = useLocalised(
    terms_and_conditions_dialogue_text_1
  );
  const termsAndConditionsDialogueText2 = useLocalised(
    terms_and_conditions_dialogue_text_2
  );
  const termsAndConditionsDialogueText3 = useLocalised(
    terms_and_conditions_dialogue_text_3
  );
  const termsAndConditionsDialogueText4 = useLocalised(
    terms_and_conditions_dialogue_text_4
  );

  return (
    <div className={clsx(styles.termsAndConditions, className)}>
      <section className={styles.textContentSection}>
        <BaseText className={styles.textContent}>
          {termsAndConditionsContent}
        </BaseText>
        <BaseText className={styles.textContent}>
          {termsAndConditionsDialogueText1}
        </BaseText>
        <BaseText className={styles.textContent}>
          {termsAndConditionsDialogueText2}
        </BaseText>
        <BaseText className={styles.textContent}>
          {termsAndConditionsDialogueText3}
        </BaseText>
        <BaseText className={styles.textContent}>
          {termsAndConditionsDialogueText4}
        </BaseText>
      </section>
      <AccordionList<TermsAndConditionsType>
        items={termsAndConditions}
        getId={item => `item-${item.id}`}
        getTitle={item => `${item.id}. ${item.title}`}
        getContent={item => [item]}
        renderContent={item => {
          const { list } = item;
          return (
            <div
              className={styles.accordionListContainer}
              key={`list-${item.id}`}
            >
              {list?.map(listItem => (
                <div
                  key={`list-item-${listItem.id}`}
                  className={styles.accordionListItem}
                >
                  <BaseText
                    color="grey-dark"
                    weight="demiBold"
                    className={styles.accordionListItemSubtitle}
                  >
                    {listItem.subtitle}
                  </BaseText>
                  <div>
                    <BaseText
                      as="div"
                      color="grey-dark"
                      className={styles.accordionListItemDescription}
                    >
                      <span
                        dangerouslySetInnerHTML={{
                          __html: listItem.description,
                        }}
                      />
                    </BaseText>
                    {listItem.innerList && (
                      <ul className={styles.accordionListItemInnerList}>
                        {listItem.innerList?.map((item, index) => (
                          <div
                            key={`inner-list-item-${index + 1}`}
                            className={styles.accordionListItem}
                          >
                            <BaseText
                              color="grey-dark"
                              weight="demiBold"
                              className={styles.accordionListItemSubtitle}
                            >
                              {item.subtitle}
                            </BaseText>
                            <BaseText
                              as="div"
                              color="grey-dark"
                              className={styles.accordionListItemDescription}
                            >
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: item.description,
                                }}
                              />
                            </BaseText>
                          </div>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        }}
      />
    </div>
  );
};

export default TermsAndConditions;
